/**
 * useMyDealsAgentTools — wires the My Deals page (deals table) into FutureBot.
 *
 * Same registration pattern as useDashboardAgentTools, with the deals-page
 * nuances baked in:
 *   - "clear" restores the page BASELINE (stage not_in ['deal-lost']), not [].
 *   - Applying a smartlist also applies its saved sort and visible columns
 *     (replicating the page's own onSelectSmartList callback).
 *   - Sort changes are persisted to the user's table preferences the same way
 *     the table header does it.
 *
 * Tool names must match functions/FutureBot/toolsets.js (crm-my-deals).
 */

import { useCallback, useMemo, useRef } from 'react';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { LEAD_SOURCES } from '../../../constants/crmConstants';
import {
  waitForIdle,
  summarizeConditions,
  resolveSmartList,
  describeSmartList,
  canManageSmartList,
} from '../../../components/FutureBot/agentToolHelpers';

const MY_DEALS_SUGGESTIONS = [
  'Show my deals closing this month over $300k',
  'Save this view as a smartlist',
  'Include lost deals in the table',
];

export function useMyDealsAgentTools(pageApi, { enabled = true } = {}) {
  const { tags, dealStages } = useCrmSettings();

  // Fresh-state refs so stable tool closures always read live values.
  const apiRef = useRef(pageApi);
  apiRef.current = pageApi;
  const settingsRef = useRef({ tags, dealStages });
  settingsRef.current = { tags, dealStages };
  const loadingRef = useRef(false);
  loadingRef.current = !!pageApi.loading;

  const settleAndCount = useCallback(async () => {
    await waitForIdle(loadingRef);
    return apiRef.current.totalCount;
  }, []);

  const applySearchEverywhere = useCallback((term) => {
    const api = apiRef.current;
    api.setTableSearchTerm(term);
    api.setActiveSearchTerm(term);
    api.setGlobalSearch(term);
  }, []);

  const applySortEverywhere = useCallback((field, direction) => {
    const api = apiRef.current;
    api.applySort(field, direction);
    // Persist like the table header does so the sort survives reloads.
    if (api.persistSort) api.persistSort(field, direction);
  }, []);

  const viewState = useCallback(() => {
    const api = apiRef.current;
    const selectedList = api.smartLists.find((l) => l.id === api.selectedListId) || null;
    return {
      filters: api.filters || [],
      filtersSummary: summarizeConditions(api.filters),
      baseline: 'stage not_in [deal-lost] (lost deals hidden by default)',
      searchTerm: api.activeSearchTerm || '',
      sort: api.sort || null,
      totalCount: api.totalCount,
      page: api.currentPage,
      pageSize: api.pageSize,
      ownerScope: api.isCRMAdmin
        ? { selectedUserId: api.selectedUserId || '', viewing: api.selectedUserId ? (api.crmUsers.find((u) => u.uid === api.selectedUserId)?.name || api.selectedUserId) : 'all users' }
        : { viewing: 'own deals (non-admin, fixed)' },
      selectedSmartList: describeSmartList(selectedList, api.currentUser?.uid),
      availableSmartLists: api.smartLists.map((l) => ({ id: l.id, name: l.name, isGlobal: !!l.isGlobal, isDefault: !!l.isDefault })),
    };
  }, []);

  // -------------------------------------------------------------------------
  // Tool implementations
  // -------------------------------------------------------------------------

  const tools = useMemo(() => ({
    get_view_state: () => viewState(),

    set_filters: async (args) => {
      const api = apiRef.current;
      const filters = Array.isArray(args?.filters) ? args.filters : [];
      api.applyFilters(filters);
      const totalCount = await settleAndCount();
      const result = { ok: true, appliedFilters: summarizeConditions(filters), totalCount };
      if (!filters.some((f) => f.field === 'stage' && f.operator === 'not_in')) {
        result.note = 'The baseline "hide lost deals" condition is not in this filter set, so lost deals may be included.';
      }
      if (apiRef.current.selectedListId) {
        result.smartListNote = 'A smartlist is still selected in the UI but the on-screen filters have now drifted from it.';
      }
      return result;
    },

    clear_filters: async () => {
      const api = apiRef.current;
      api.applyFilters(api.baselineFilters);
      applySearchEverywhere('');
      api.setSelectedListId(null);
      const totalCount = await settleAndCount();
      return {
        ok: true,
        totalCount,
        note: 'Restored the baseline view (lost deals hidden) and cleared the search.',
      };
    },

    set_search: async (args) => {
      const term = typeof args?.searchTerm === 'string' ? args.searchTerm : '';
      applySearchEverywhere(term);
      const totalCount = await settleAndCount();
      return { ok: true, searchTerm: term, totalCount };
    },

    set_sort: async (args) => {
      const { field, direction } = args || {};
      if (!field || !['asc', 'desc'].includes(direction)) {
        return { error: 'set_sort needs field and direction ("asc"|"desc").' };
      }
      applySortEverywhere(field, direction);
      await waitForIdle(loadingRef);
      return { ok: true, sort: { field, direction } };
    },

    set_owner_scope: async (args) => {
      const api = apiRef.current;
      if (!api.isCRMAdmin) {
        return { error: 'Only CRM admins can change the owner scope. This user always sees their own deals.' };
      }
      const userId = typeof args?.userId === 'string' ? args.userId : '';
      if (userId && !api.crmUsers.some((u) => u.uid === userId)) {
        return { error: `Unknown userId "${userId}". Use an id from the users reference data, or "" for all users.` };
      }
      api.setSelectedUserId(userId);
      const totalCount = await settleAndCount();
      const viewing = userId ? (api.crmUsers.find((u) => u.uid === userId)?.name || userId) : 'all users';
      return { ok: true, viewing, totalCount };
    },

    list_smart_lists: () => {
      const api = apiRef.current;
      return {
        smartLists: api.smartLists.map((l) => describeSmartList(l, api.currentUser?.uid)),
        selectedSmartListId: api.selectedListId || null,
      };
    },

    apply_smart_list: async (args) => {
      const api = apiRef.current;
      const { list, error } = resolveSmartList(api.smartLists, args || {});
      if (error) return { error };
      // Replicate the page's own smartlist apply: filters + sort + columns.
      api.setSelectedListId(list.id);
      api.applyFilters(list.filters || []);
      if (list.sortBy) {
        applySortEverywhere(list.sortBy, list.sortDirection || 'desc');
      }
      if (Array.isArray(list.visibleColumns) && list.visibleColumns.length > 0 && api.applyVisibleColumns) {
        api.applyVisibleColumns(list.visibleColumns);
      }
      const totalCount = await settleAndCount();
      return { ok: true, applied: describeSmartList(list, api.currentUser?.uid), totalCount };
    },

    create_smart_list: async (args) => {
      const api = apiRef.current;
      const { name, filters, isGlobal, setAsDefault } = args || {};
      if (isGlobal && !api.isCRMAdmin) {
        return { error: 'Only CRM admins can create global (company-wide) smartlists. Create it as personal instead.' };
      }
      const clash = api.smartLists.find((l) => (l.name || '').trim().toLowerCase() === String(name).trim().toLowerCase());
      if (clash) {
        return { error: `A smartlist named "${clash.name}" already exists (id: ${clash.id}). Use update_smart_list, or pick another name.` };
      }
      const filtersToSave = Array.isArray(filters) ? filters : (api.filters || []);
      try {
        const newList = await api.createSmartList({
          name: String(name).trim(),
          filters: filtersToSave,
          isGlobal: !!isGlobal,
          sortBy: api.sort?.field,
          sortDirection: api.sort?.direction,
          visibleColumns: api.getVisibleColumns ? api.getVisibleColumns() : undefined,
        });
        api.setSelectedListId(newList.id);
        if (setAsDefault) await api.setAsDefault(newList.id);
        return {
          ok: true,
          smartListId: newList.id,
          name: newList.name,
          savedFilters: summarizeConditions(filtersToSave),
          isGlobal: !!isGlobal,
          isDefault: !!setAsDefault,
        };
      } catch (err) {
        return { error: `Could not save the smartlist: ${err.message}` };
      }
    },

    update_smart_list: async (args) => {
      const api = apiRef.current;
      const { list, error } = resolveSmartList(api.smartLists, args || {});
      if (error) return { error };
      if (!canManageSmartList(list, { userId: api.currentUser?.uid, isCRMAdmin: api.isCRMAdmin })) {
        return { error: `You can only edit your own smartlists${api.isCRMAdmin ? ' or global ones' : ''}. "${list.name}" belongs to someone else.` };
      }
      const { newName, filters, useCurrentFilters, isGlobal } = args || {};
      if (isGlobal !== undefined && isGlobal !== list.isGlobal && !api.isCRMAdmin) {
        return { error: 'Only CRM admins can change whether a smartlist is global.' };
      }
      const updates = {};
      if (newName) updates.name = String(newName).trim();
      if (useCurrentFilters) updates.filters = api.filters || [];
      else if (Array.isArray(filters)) updates.filters = filters;
      if (typeof isGlobal === 'boolean') updates.isGlobal = isGlobal;
      if (Object.keys(updates).length === 0) {
        return { error: 'Nothing to update — pass newName, filters, useCurrentFilters, or isGlobal.' };
      }
      try {
        const updated = await api.updateSmartList(list.id, updates);
        if (updates.filters && api.selectedListId === list.id && !useCurrentFilters) {
          api.applyFilters(updates.filters);
          await waitForIdle(loadingRef);
        }
        return { ok: true, smartList: describeSmartList(updated, api.currentUser?.uid) };
      } catch (err) {
        return { error: `Could not update the smartlist: ${err.message}` };
      }
    },

    delete_smart_list: async (args) => {
      const api = apiRef.current;
      const { list, error } = resolveSmartList(api.smartLists, args || {});
      if (error) return { error };
      if (!canManageSmartList(list, { userId: api.currentUser?.uid, isCRMAdmin: api.isCRMAdmin })) {
        return { error: `You can only delete your own smartlists${api.isCRMAdmin ? ' or global ones' : ''}. "${list.name}" belongs to someone else.` };
      }
      const wasSelected = api.selectedListId === list.id;
      try {
        await api.deleteSmartList(list.id);
        return {
          ok: true,
          deleted: list.name,
          note: wasSelected ? 'That list was selected; the current filters remain on screen but the selection was cleared.' : undefined,
        };
      } catch (err) {
        return { error: `Could not delete the smartlist: ${err.message}` };
      }
    },
  }), [viewState, settleAndCount, applySearchEverywhere, applySortEverywhere]);

  // -------------------------------------------------------------------------
  // Page context for the system prompt
  // -------------------------------------------------------------------------

  const getContext = useCallback(async () => {
    const api = apiRef.current;
    const { tags: tagList, dealStages: stageList } = settingsRef.current;

    const state = viewState();
    const referenceData = {
      stages: (stageList || []).map((s) => ({ value: s.value, label: s.label })),
      tags: (tagList || []).map((t) => t.value),
      sources: LEAD_SOURCES.map((s) => s.value),
      smartLists: api.smartLists.map((l) => ({ id: l.id, name: l.name, isGlobal: !!l.isGlobal, isDefault: !!l.isDefault })),
    };
    if (api.isCRMAdmin) {
      referenceData.users = (api.crmUsers || []).map((u) => ({ id: u.uid, name: u.name }));
    }

    const scopeLabel = api.isCRMAdmin ? `Owner scope: ${state.ownerScope.viewing}.` : 'Scoped to own deals.';
    return {
      referenceData,
      stateSummary:
        `Deals table: ${state.totalCount ?? '?'} result(s), page ${state.page}. ` +
        `Filters: ${state.filtersSummary}. Search: ${state.searchTerm ? `"${state.searchTerm}"` : 'none'}. ${scopeLabel}` +
        (state.selectedSmartList ? ` Selected smartlist: "${state.selectedSmartList.name}".` : ''),
    };
  }, [viewState]);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-my-deals',
      title: 'My Deals',
      tools,
      getContext,
      suggestions: MY_DEALS_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration };
}

export default useMyDealsAgentTools;
